import React, { useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { SDLogo, Avatar, Spinner } from '@/components/atoms';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileState } from '@/contexts/ProfileContext';
import { profile as profileApi } from '@/lib/api';
import TransitionOverlay from './TransitionOverlay';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', end: true, icon: 'home' },
  { to: '/dashboard/skills', label: 'Skills Profile', icon: 'star' },
  { to: '/dashboard/roles', label: 'Role Explorer', icon: 'briefcase' },
  { to: '/dashboard/network', label: 'Network In', icon: 'users' },
  { to: '/dashboard/jd', label: 'JD Analyzer', icon: 'file' },
  { to: '/dashboard/progress', label: 'My Progress', icon: 'check' },
];

function Icon({ name }) {
  const stroke = 'currentColor';
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'home': return <svg {...common}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4a1 1 0 0 1-1-1v-6h-4v6a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2z"/></svg>;
    case 'star': return <svg {...common}><polygon points="12 2 15 9 22 9.5 17 14.5 18.5 21 12 17.5 5.5 21 7 14.5 2 9.5 9 9 12 2"/></svg>;
    case 'briefcase': return <svg {...common}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>;
    case 'file': return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case 'check': return <svg {...common}><polyline points="20 6 9 17 4 12"/></svg>;
    case 'users': return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    default: return null;
  }
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { savedProfile, analysis, progress, reload, loading, activeMarketKey, markets, switchToMarket } = useProfileState();
  const navigate = useNavigate();

  // Redirect to onboarding ONLY when we know the user has never completed
  // it. Transient `analysis === null` (mid-fetch during market/pivot switch,
  // failed API call, etc.) must NOT boot the user off the dashboard.
  // Signals of "never analyzed":
  //   • no savedProfile at all → they never finished onboarding step 1
  //   • savedProfile exists but no analysis AND no known markets/pivots →
  //     onboarding was abandoned before the first analysis kicked off
  // Anyone with `savedProfile` + a completed initial analysis stays here
  // even if the current fetch returned null.
  useEffect(() => {
    if (loading) return;
    const neverAnalyzed = !savedProfile;
    if (neverAnalyzed) {
      navigate('/onboarding/connect', { replace: true });
    }
  }, [loading, savedProfile, navigate]);

  const totalMilestones = useMemo(() => {
    const c2 = analysis?.call2 || {};
    return (c2.milestones_critical?.length || 0) + (c2.milestones_quick?.length || 0) + (c2.milestones_build?.length || 0) + (progress?.saved_jd_actions?.length || 0);
  }, [analysis, progress]);
  const doneCount = progress?.completed_milestones?.length || 0;
  const pctDone = totalMilestones > 0 ? Math.min(100, Math.round((doneCount / totalMilestones) * 100)) : 0;

  const newAssessment = async () => {
    if (!window.confirm('Start a new assessment? This deletes your current profile, analysis and progress.')) return;
    await profileApi.reset();
    await reload();
    navigate('/onboarding/connect', { replace: true });
  };

  if (loading || !savedProfile) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={42} /></div>;
  }

  return (
    <div className="min-h-screen flex bg-sd-ivory">
      {/* Desktop sidebar (md and up) */}
      <aside className="hidden md:flex flex-col w-[256px] bg-white border-r border-sd-light-gray sticky top-0 h-screen">
        <div className="px-6 py-6"><SDLogo variant="brand" /></div>

        <div className="px-5 py-4 mx-4 mb-3 rounded-card bg-sd-cream flex items-center gap-3">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-[42px] h-[42px] rounded-full object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <Avatar name={savedProfile?.name || user?.name} size={42} />
          )}
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-sd-black truncate">{savedProfile?.name || user?.name || user?.email}</div>
            <div className="text-[11px] text-sd-mid-gray truncate">{savedProfile?.career_stage || 'Member'}</div>
          </div>
        </div>

        {/* Active market indicator — only shows when the user has switched away from home */}
        {activeMarketKey && (() => {
          const active = markets.find((m) => m.market_key === activeMarketKey);
          if (!active) return null;
          return (
            <div className="sd-sidebar-market mx-4 mb-3" data-testid="sidebar-active-market">
              <div className="sd-sidebar-market__label">Currently exploring</div>
              <div className="sd-sidebar-market__value">
                <span aria-hidden>🌐</span>
                <span className="truncate">{active.target_market}</span>
              </div>
              {!active.work_authorized_target && (
                <div className="sd-sidebar-market__visa">Visa needed</div>
              )}
              <button
                type="button"
                onClick={() => switchToMarket('')}
                className="sd-sidebar-market__reset"
                data-testid="sidebar-market-reset"
              >
                Back to {savedProfile?.location || 'home'}
              </button>
            </div>
          );
        })()}

        <nav className="flex-1 px-3 space-y-0.5">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all
                ${isActive
                  ? 'bg-sd-primary text-white shadow-card-hover'
                  : 'text-sd-dark-gray hover:bg-sd-cream'}`
              }
            >
              <Icon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-5 pb-2">
          <div className="text-[11px] uppercase tracking-wider text-sd-mid-gray font-medium mb-1.5">Plan progress</div>
          <div className="h-2 rounded-full bg-sd-light-gray overflow-hidden">
            <div
              className="h-full bg-sd-primary"
              style={{ width: `${pctDone}%`, transition: 'width 800ms cubic-bezier(.2,.8,.2,1)' }}
            />
          </div>
          <div className="text-[12px] text-sd-mid-gray mt-1.5">{doneCount} / {totalMilestones} milestones · {pctDone}%</div>
        </div>

        <div className="px-4 py-4 space-y-1">
          <button className="sd-btn-secondary w-full justify-center !py-2 text-[12.5px]" onClick={newAssessment} data-testid="sidebar-new-assessment">
            + New Assessment
          </button>
          <button className="w-full text-left px-3 py-2 text-[12px] text-sd-mid-gray hover:text-sd-black" onClick={logout} data-testid="sidebar-logout">
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto pb-24 md:pb-0">
        {/* Mobile top bar with logo + progress ring + sign-out */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-white border-b border-sd-light-gray">
          <SDLogo variant="brand" />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-[92px] h-1.5 rounded-full bg-sd-light-gray overflow-hidden">
                <div className="h-full bg-sd-primary" style={{ width: `${pctDone}%`, transition: 'width 800ms cubic-bezier(.2,.8,.2,1)' }} />
              </div>
              <span className="text-[11px] font-mono text-sd-mid-gray">{pctDone}%</span>
            </div>
            {/* Sign-out icon button — mobile equivalent of the desktop sidebar
                Sign-out link. Uses the standard "logout" glyph so it reads as
                a session action, not a navigation. */}
            <button
              type="button"
              onClick={logout}
              aria-label="Sign out"
              title="Sign out"
              data-testid="mobile-logout"
              className="w-9 h-9 rounded-full flex items-center justify-center text-sd-mid-gray hover:text-sd-black hover:bg-sd-light-gray transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-w-[960px] mx-auto px-4 md:px-10 py-6 md:py-10 animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom-tab nav (below md) */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-sd-light-gray flex items-stretch justify-around"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="mobile-bottom-nav"
      >
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[10.5px] font-medium transition-colors min-h-[52px] ${isActive ? 'text-sd-primary' : 'text-sd-mid-gray hover:text-sd-black'}`
            }
          >
            <Icon name={item.icon} />
            <span className="leading-none">{item.label.split(' ')[0]}</span>
          </NavLink>
        ))}
      </nav>

      {/* Global transition overlay — shown while ProfileContext is swapping a market analysis in.
          Sits above the content and dims the current view so the flip is felt as one smooth
          "Refreshing your insights…" / "Calibrated for [City]…" moment. */}
      <TransitionOverlay />
    </div>
  );
}
